import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import styles from '../styles.module.css';
import { handleChangeUserDataType, handleChangeUserType } from './types';
import { statusContainer, UserForm } from './userForm';
import { createUserSchema } from './utils';
import { userFormData } from '../../../constants/user';
import UserIcon from '@/assets/icons/user.svg?react';
import {
  AdminCardButtons,
  AdminCardHeader
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Button } from '@/components/Form/Button';
import { getZodSchemaErrors } from '@/components/Form/Input/utils';
import { adminToolPaths } from '@/constants/adminTool';
import { useModal } from '@/context/ModalContext';
import { useToast } from '@/context/ToastContext';
import { useCreateUser } from '@/hooks/services/Users';

function NewUser() {
  const navigate = useNavigate();
  const { showTypedModal } = useModal();
  const { showToast } = useToast();
  const [initialLoading, setInitialLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
  const [onSubmit, setOnSubmit] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const { mutate: createUser, isPending, error, reset } = useCreateUser();

  const [formData, setFormData] = useState(userFormData);

  const setParsedZodData = (formData: any) => {
    const parsed = createUserSchema.safeParse({
      ...formData
    });

    if (!parsed.success) {
      const errs = getZodSchemaErrors(parsed);
      setFormErrors(errs);
    } else {
      setFormErrors({});
    }

    return parsed;
  };

  const handleSelectFormData = (selectedData: handleChangeUserDataType[]) => {
    const newFormData = {
      ...formData,
      ...Object.fromEntries(
        selectedData.map(({ field, value }) => [field, value])
      )
    };

    if (onSubmit) {
      setParsedZodData(newFormData);
    }
    setFormData(newFormData);
  };

  const handleChangeFormData: handleChangeUserType = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    if (onSubmit) {
      setParsedZodData(newFormData);
    }
    setFormData(newFormData);
  };

  const onCreate = () => {
    setOnSubmit(true);
    const parsed = setParsedZodData(formData);

    if (parsed.success) {
      setFormErrors({});
      createUser(formData, {
        onSuccess: () => {
          showTypedModal({
            type: 'success',
            title: 'User was successfully created'
          });
          setTimeout(() => {
            navigate(
              `${adminToolPaths.users.root}/${formData.username?.toLowerCase()}`
            );
          }, 3000);
        },
        onError: error => {
          console.error('Error saving user:', error);
        }
      });
    }
  };

  const onCancel = () => {
    setOnSubmit(false);
    setFormErrors({});
    setFormData(userFormData);
    navigate(adminToolPaths.users.root);
  };

  useEffect(() => {
    if (error) {
      showToast(error.message || 'An error occurred while creating user', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => {
          reset();
          onCreate();
        }
      });
    }
  }, [error]);

  if (initialLoading) {
    return <ContainerLoader />;
  }

  return (
    <div className={styles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminCardHeader
          left={
            <>
              <UserIcon className={styles.adminIconHeader} />
              <h2 className={styles.adminTitle}>New User</h2>
            </>
          }
          right={statusContainer({
            formData,
            handleChangeFormData
          })}
        />
        <UserForm
          container="create"
          formData={formData}
          handleChangeFormData={handleChangeFormData}
          handleSelectFormData={handleSelectFormData}
          formErrors={formErrors}
        />
      </div>

      <AdminCardButtons>
        <Button
          onClick={() => onCancel()}
          color="blue"
          solid={false}
          label="CANCEL"
          size="large"
        />
        <Button
          onClick={() => onCreate()}
          color="blue"
          label="SAVE"
          size="large"
          loading={isPending}
        />
      </AdminCardButtons>
    </div>
  );
}

export default NewUser;
